import type {
    FindingPlanumDocType,
    FindingProfileDocType
} from '@ast-data/shared';

/**
 * How a record points at Befunde: the three id sets behind every Befund picker
 * in the app (Foto, Zeichenblatt, Fund, Gesamtbeschreibung, and the
 * stratigraphy relations on a description).
 *
 * Pure logic, kept out of the picker component so it runs in the node test
 * environment without pulling MUI in.
 */
export interface FindingRefs {
    /** Specific Planum descriptions (finding_planum ids). */
    planumIds: string[];
    /** Specific Profil descriptions (finding_profile ids). */
    profileIds: string[];
    /** Whole Befunde (finding ids). */
    findingIds: string[];
}

const EMPTY: string[] = [];

/** Whether any of the three ref arrays hold a value. */
export function hasFindingRefs(refs: FindingRefs): boolean {
    return (
        refs.planumIds.length > 0 ||
        refs.profileIds.length > 0 ||
        refs.findingIds.length > 0
    );
}

/**
 * A record's own Lage, typed in when it lies in no Befund — the Fund, the
 * Probe, the Foto, the Zeichenblatt and the Gesamtbeschreibung each carry these
 * four. Either these or a Befund reference, never both.
 */
export const OWN_LOCATION_FIELDS = [
    'area',
    'trench',
    'planum',
    'profile'
] as const;

/** Whether any of the {@link OWN_LOCATION_FIELDS} values is filled in. */
export function isOwnLocationFilled(
    values: readonly (string | undefined)[]
): boolean {
    return values.some((value) => (value ?? '').trim() !== '');
}

/**
 * The own Lage blanked — what a record linked to a Befund saves. The form hides
 * the Lage once a link is chosen, but its inputs keep their values; the Fund,
 * Probe, Foto and Zeichenblatt all write this over them, so a record never
 * carries both. (The ExcaBook import already writes the Lage only without a
 * link.)
 */
export const NO_OWN_LOCATION = {
    area: '',
    trench: '',
    planum: '',
    profile: ''
} as const;

/** A stable empty ref set (for defaults). */
export function emptyFindingRefs(): FindingRefs {
    return { planumIds: EMPTY, profileIds: EMPTY, findingIds: EMPTY };
}

/**
 * Every Befund a ref set touches, as a WHOLE-Befund ref set: the `findingIds`
 * it already holds plus the Befund behind each referenced Planum/Profil,
 * deduped, with the two description arrays emptied.
 *
 * A Gesamtbeschreibung used to store the single descriptions it covered and now
 * covers Befunde as units. Folding on read keeps those older records readable;
 * writing the folded set back normalises them. A ref whose description is not
 * on this device drops out rather than being carried as an id.
 */
export function foldRefsToFindings(
    refs: FindingRefs,
    planums: FindingPlanumDocType[],
    profiles: FindingProfileDocType[]
): FindingRefs {
    const findingIds = new Set(refs.findingIds);
    if (refs.planumIds.length > 0) {
        const findingIdById = new Map(
            planums.map((planum) => [planum.id, planum.findingId])
        );
        for (const id of refs.planumIds) {
            const findingId = findingIdById.get(id);
            if (findingId) {
                findingIds.add(findingId);
            }
        }
    }
    if (refs.profileIds.length > 0) {
        const findingIdById = new Map(
            profiles.map((profile) => [profile.id, profile.findingId])
        );
        for (const id of refs.profileIds) {
            const findingId = findingIdById.get(id);
            if (findingId) {
                findingIds.add(findingId);
            }
        }
    }
    return { planumIds: [], profileIds: [], findingIds: [...findingIds] };
}
